/**
 * Access matrix — the role/page grid derived from the application's own route table.
 *
 * Nothing here restates the access rules: the grid is built from `routes` in app.routes.ts
 * and from the same inheritance the AuthGuard applies, so the screen cannot drift from the
 * access it reports. If a route's `data.roles` changes, this matrix changes with it.
 */

/** The access roles, each backed by one Entra ID security group. */
export type AccessRole =
  | 'admin'
  | 'pm'
  | 'finance'
  | 'pc'
  | 'software'
  | 'accounting'
  | 'marketing'
  | 'sales'
  | 'it'
  | 'lnd'
  | 'operations'
  | 'support';

export interface RoleMeta {
  /** The value used in a route's `data.roles` array. */
  role: AccessRole;
  /** Human label for the column header. */
  label: string;
  /** Key into `environment.msal.*` holding this role's Entra group id. */
  groupIdKey: string;
}

/**
 * Column order for the matrix. Mirrors the declaration order in auth-config.ts so the
 * screen reads the same way as the configuration it describes.
 */
export const ACCESS_ROLES: RoleMeta[] = [
  { role: 'admin', label: 'Admin', groupIdKey: 'adminGroupId' },
  { role: 'pm', label: 'PM', groupIdKey: 'pmGroupId' },
  { role: 'finance', label: 'Finance', groupIdKey: 'financeGroupId' },
  { role: 'pc', label: 'PC', groupIdKey: 'pcGroupId' },
  { role: 'software', label: 'Software', groupIdKey: 'softwareGroupId' },
  { role: 'accounting', label: 'Accounting', groupIdKey: 'accountingGroupId' },
  { role: 'marketing', label: 'Marketing', groupIdKey: 'marketingGroupId' },
  { role: 'sales', label: 'Sales', groupIdKey: 'salesGroupId' },
  { role: 'it', label: 'IT', groupIdKey: 'itGroupId' },
  { role: 'lnd', label: 'L&D', groupIdKey: 'lndGroupId' },
  { role: 'operations', label: 'Operations', groupIdKey: 'operationsGroupId' },
  { role: 'support', label: 'Support', groupIdKey: 'supportGroupId' }
];

/**
 * How a role reaches a page.
 *  - 'direct'    the role is named in the route's `data.roles`
 *  - 'inherited' the role is not named, but the guard grants it anyway (admin over
 *                pm/finance/pc). Shown distinctly so the implicit grants are visible
 *                rather than looking like someone listed admin on every route.
 *  - 'none'      no access
 */
export type AccessKind = 'direct' | 'inherited' | 'none';

export interface AccessCell {
  role: AccessRole;
  kind: AccessKind;
}

export interface AccessMatrixRow {
  /** Route path as registered, with a leading slash (''  is shown as '/'). */
  path: string;
  /** Friendly page name from the app's own route-title map, else the path. */
  title: string;
  /** Functional area, used to group the table. */
  area: string;
  /** Roles exactly as written in `data.roles`. */
  declaredRoles: AccessRole[];
  /** One cell per role in ACCESS_ROLES order. */
  cells: AccessCell[];
  /** Count of roles with any access — the "how exposed is this page" number. */
  accessCount: number;
}

/**
 * Roles implied by holding another role, taken from the AuthGuard's switch:
 *   case 'pm':      user.isAdmin || user.isPM
 *   case 'finance': user.isAdmin || user.isFinance
 *   case 'pc':      user.isAdmin || user.isPc
 * Every other case tests its own flag only, so admin does NOT reach software,
 * accounting, marketing, sales, it or lnd pages unless listed explicitly.
 */
export const ROLE_IMPLIES: Record<AccessRole, AccessRole[]> = {
  admin: ['pm', 'finance', 'pc'],
  pm: [],
  finance: [],
  pc: [],
  software: [],
  accounting: [],
  marketing: [],
  sales: [],
  it: [],
  lnd: [],
  operations: [],
  support: []
};
